/**
 * Plumbing shared by the Developer and Marketplace panels. Nothing here
 * renders — the panels own their markup; this file owns what they both
 * repeat. Local rather than imported from `Pages/Integrations/lib.ts` —
 * that file's own header explains why each feature keeps its own copy: the
 * shape is shared, the strings and status vocabularies are not.
 */

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

/**
 * Surface the API's own message rather than a generic failure. Checks both a
 * raw axios error's `.response.data.message` (every direct mutation in this
 * feature) and RTK Query's own `{ status, message }` shape (every list/detail
 * read, already flattened once by `axiosBaseQuery` in `Redux/baseApi.ts`) —
 * see `Pages/Integrations/lib.ts`'s copy of this function for the full reasoning.
 */
export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  const rtkMessage = (error as { message?: unknown } | null)?.message;
  if (typeof rtkMessage === 'string' && rtkMessage.trim()) return rtkMessage;
  return fallback;
}

/** The stable code on a coded refusal — e.g. `SCOPES_MISMATCH` — when the API sent one. */
export function errorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

export function formatDateTime(value: string | Date | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** `day` is a UTC date string (`YYYY-MM-DD`) from `api_usage_counters`, not a full timestamp. */
export function formatUsageDay(day: string, locale: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

const APP_STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  PENDING_REVIEW: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  SUSPENDED: 'danger',
};

const INSTALLATION_STATUS_TONE: Record<string, Tone> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  UNINSTALLED: 'neutral',
};

const CLIENT_STATUS_TONE: Record<string, Tone> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  REVOKED: 'danger',
};

function toneFrom(table: Record<string, Tone>, status?: string | null): Tone {
  if (!status) return 'neutral';
  return table[status] ?? 'neutral';
}

export const toneForAppStatus = (s?: string | null) => toneFrom(APP_STATUS_TONE, s);
export const toneForInstallationStatus = (s?: string | null) => toneFrom(INSTALLATION_STATUS_TONE, s);
export const toneForClientStatus = (s?: string | null) => toneFrom(CLIENT_STATUS_TONE, s);

/** `a` and `b` name the same set, regardless of order — mirrors `sameScopeSet` in `marketplace-installations.service.ts`, the exact comparison "approved never widens consented" depends on. */
export function sameScopeSet(a: readonly string[], b: readonly string[]): boolean {
  const left = [...a].sort();
  const right = [...b].sort();
  return left.length === right.length && left.every((s, i) => s === right[i]);
}
